import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IosShareIcon from "@mui/icons-material/IosShare";
import CloseIcon from "@mui/icons-material/Close";
import "./ShareQR.css";

const SITE_URL = "https://renuairhub.com";

function ShareQR() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCopied(false);
  }, []);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(SITE_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  }, []);

  const handlePrint = useCallback(() => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank", "width=400,height=600");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>RenuAirHub - QR Code</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #fff; }
          .card { text-align: center; padding: 40px 32px; border: 2px solid #5b7c99; border-radius: 16px; max-width: 320px; }
          .logo { font-size: 28px; font-weight: 700; color: #5b7c99; margin-bottom: 8px; letter-spacing: 0.5px; }
          .tagline { font-size: 13px; color: #666; margin-bottom: 24px; line-height: 1.4; }
          .qr { margin: 0 auto 20px; }
          .airports { font-size: 11px; color: #888; line-height: 1.6; margin-bottom: 16px; }
          .url { font-size: 14px; font-weight: 600; color: #5b7c99; letter-spacing: 0.3px; }
          .scan-text { font-size: 12px; color: #444; margin-bottom: 12px; font-weight: 500; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="logo">RenuAirHub</div>
          <div class="tagline">${t("share.tagline")}</div>
          <div class="qr">${printContent.querySelector(".shareqr-code").innerHTML}</div>
          <div class="scan-text">${t("share.scanToAccess")}</div>
          <div class="airports">Lagos · Abidjan · Casablanca & more</div>
          <div class="url">renuairhub.com</div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  }, [t]);

  const handleNativeShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: "RenuAirHub",
        text: t("share.nativeShareText"),
        url: SITE_URL,
      });
    }
  }, [t]);

  return (
    <>
      <button
        className="shareqr-trigger"
        onClick={handleOpen}
        aria-label={t("share.title")}
      >
        <QrCode2Icon style={{ fontSize: 16 }} />
        <span>{t("share.title")}</span>
      </button>

      {isOpen && (
        <div className="shareqr-overlay" onClick={handleClose}>
          <div
            className="shareqr-modal"
            onClick={(e) => e.stopPropagation()}
            ref={printRef}
          >
            <button
              className="shareqr-close"
              onClick={handleClose}
              aria-label={t("bookFlight.close")}
            >
              <CloseIcon fontSize="small" />
            </button>

            <div className="shareqr-content">
              <h3 className="shareqr-title">{t("share.modalTitle")}</h3>
              <p className="shareqr-subtitle">{t("share.modalSubtitle")}</p>

              <div className="shareqr-code">
                <QRCodeSVG
                  value={SITE_URL}
                  size={180}
                  level="M"
                  includeMargin={false}
                  fgColor="#5b7c99"
                />
              </div>

              <p className="shareqr-url">renuairhub.com</p>

              <div className="shareqr-actions">
                <button
                  className="shareqr-btn shareqr-btn-print"
                  onClick={handlePrint}
                >
                  <PrintIcon style={{ fontSize: 18 }} />
                  <span>{t("share.print")}</span>
                </button>
                <button
                  className="shareqr-btn shareqr-btn-copy"
                  onClick={handleCopyLink}
                >
                  <ContentCopyIcon style={{ fontSize: 18 }} />
                  <span>
                    {copied ? t("share.copied") : t("share.copyLink")}
                  </span>
                </button>
                {navigator.share && (
                  <button
                    className="shareqr-btn shareqr-btn-share"
                    onClick={handleNativeShare}
                  >
                    <IosShareIcon style={{ fontSize: 18 }} />
                    <span>{t("share.share")}</span>
                  </button>
                )}
              </div>

              <p className="shareqr-hint">{t("share.hint")}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ShareQR;
